<template>
  <div class="chat-panel">
    <div class="buttons are-small is-flex-wrap chat-tabs">
      <button
        type="button"
        class="button"
        v-bind:class="{ 'is-primary': activeTab === 'global' }"
        v-on:click="switchTab('global')"
      >
        Общий
      </button>
      <button
        type="button"
        class="button"
        v-bind:class="{ 'is-primary': activeTab === 'system' }"
        v-bind:disabled="!systemRoom"
        v-on:click="switchTab('system')"
      >
        Система
      </button>
      <button
        v-for="room in privateRooms"
        v-bind:key="room.id"
        type="button"
        class="button"
        v-bind:class="{ 'is-primary': activeTab === room.id }"
        v-on:click="switchTab(room.id)"
      >
        {{ room.title }}
      </button>
    </div>

    <div class="chat-body">
      <div class="chat-main">
        <ChatWindow
          v-if="activeRoom"
          ref="chatWindow"
          v-bind:messages="activeMessages"
          v-bind:reply-to="replyTo"
          v-bind:sending="sending"
          v-bind:current-user-id="user.id"
          v-bind:show-private-button="activeTab === 'global' || activeTab === 'system'"
          v-on:send="sendMessage"
          v-on:reply="setReply"
          v-on:cancel-reply="clearReply"
          v-on:private-chat="requestPrivateChat"
        />
        <p v-else class="no-room">Чат недоступен</p>
      </div>

      <ChatPlayerList
        v-if="activeTab === 'global' || activeTab === 'system'"
        v-bind:title="playerListTitle"
        v-bind:players="activePlayers"
        v-bind:loading="playersLoading"
        v-on:private-chat="requestPrivateChat"
      />
    </div>
  </div>
</template>

<script>
import ChatWindow from './ChatWindow'
import ChatPlayerList from './ChatPlayerList'
import ChatService from '../../services/ChatService'
import { CHAT_CHANNEL, CHAT_CHANNEL_NAME } from '../../channels/chat_channel'

export default {
  components: { ChatWindow, ChatPlayerList },
  channels: {
    ChatChannel: {
      received (payload) {
        this.handleChatChannelMessage(payload)
      },
    },
  },
  props: {
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      rooms: [],
      messagesByRoom: {},
      activeTab: 'global',
      replyTo: null,
      sending: false,
      systemPlayers: [],
      globalPlayers: [],
      playersLoading: false,
      starId: null,
      cableSubscribed: false,
    }
  },
  computed: {
    globalRoom () {
      return this.rooms.find(r => r.kind === 'global')
    },
    systemRoom () {
      return this.rooms.find(r => r.kind === 'system')
    },
    privateRooms () {
      return this.rooms.filter(r => r.kind === 'direct')
    },
    activeRoom () {
      if (this.activeTab === 'global') return this.globalRoom
      if (this.activeTab === 'system') return this.systemRoom
      return this.rooms.find(r => r.id === this.activeTab)
    },
    activeMessages () {
      if (!this.activeRoom) return []
      return this.messagesByRoom[this.activeRoom.id] || []
    },
    activePlayers () {
      return this.activeTab === 'global' ? this.globalPlayers : this.systemPlayers
    },
    playerListTitle () {
      return this.activeTab === 'global' ? 'Игроки в общем чате' : 'Игроки в системе'
    },
  },
  watch: {
    user: {
      immediate: true,
      handler (value) {
        const nextStarId = value?.user_location?.star_id || null
        if (nextStarId === this.starId) return

        this.starId = nextStarId
        if (this.cableSubscribed) {
          this.performResubscribe()
        }
        this.reloadRoomsData()
      },
    },
  },
  created () {
    this.loadRooms()
    this.loadGlobalPlayers()
    this.$root.$on('chat-message', this.handleChatMessage)
    this.$root.$on('location-changed', this.handleLocationChanged)
    this.$root.$on('private-chat-opened', this.handlePrivateChatOpened)
  },
  beforeDestroy () {
    this.unsubscribeCable()
    this.$root.$off('chat-message', this.handleChatMessage)
    this.$root.$off('location-changed', this.handleLocationChanged)
    this.$root.$off('private-chat-opened', this.handlePrivateChatOpened)
  },
  methods: {
    async loadRooms () {
      try {
        await this.reloadRoomsData()
      } catch (_error) {
        // ignore
      }
      this.subscribeCable()
    },
    async reloadRoomsData () {
      const data = await ChatService.listRooms()
      this.rooms = data.data || []
      await Promise.all(this.rooms.map(room => this.loadMessages(room.id)))
    },
    async loadMessages (roomId) {
      try {
        const data = await ChatService.getMessages(roomId)
        this.$set(this.messagesByRoom, roomId, data.data || [])
      } catch (_error) {
        this.$set(this.messagesByRoom, roomId, [])
      }
    },
    async loadSystemPlayers () {
      this.playersLoading = true
      try {
        const data = await ChatService.getSystemPlayers()
        this.systemPlayers = data.data || []
      } catch (_error) {
        this.systemPlayers = []
      } finally {
        this.playersLoading = false
      }
    },
    async loadGlobalPlayers () {
      this.playersLoading = true
      try {
        const data = await ChatService.getGlobalPlayers()
        this.globalPlayers = data.data || []
      } catch (_error) {
        this.globalPlayers = []
      } finally {
        this.playersLoading = false
      }
    },
    loadActivePlayers () {
      if (this.activeTab === 'global') {
        this.loadGlobalPlayers()
      } else if (this.activeTab === 'system') {
        this.loadSystemPlayers()
      }
    },
    switchTab (tab) {
      this.activeTab = tab
      this.clearReply()
      if (tab === 'global' || tab === 'system') {
        this.loadActivePlayers()
      }
    },
    setReply (message) {
      this.replyTo = message
      this.$nextTick(() => {
        this.$refs.chatWindow?.focusInput()
      })
    },
    clearReply () {
      this.replyTo = null
    },
    sendMessage (body) {
      if (!this.activeRoom || this.sending) return
      if (!this.cableSubscribed) {
        this.subscribeCable()
      }
      this.sending = true
      try {
        this.$cable.perform({
          channel: CHAT_CHANNEL_NAME,
          action: 'send_message',
          data: {
            chat_room_id: this.activeRoom.id,
            body,
            reply_to_id: this.replyTo?.id || null,
          },
        })
        this.clearReply()
      } catch (_error) {
        this.sending = false
        this.$buefy.toast.open({
          message: 'Чат не подключён. Обновите страницу.',
          type: 'is-danger',
        })
      }
    },
    appendMessage (message) {
      if (!message?.chat_room_id) return
      const roomMessages = this.messagesByRoom[message.chat_room_id] || []
      if (roomMessages.some(m => m.id === message.id)) return
      this.$set(
        this.messagesByRoom,
        message.chat_room_id,
        [...roomMessages, message]
      )
    },
    handleChatMessage (message) {
      this.appendMessage(message)
      this.sending = false
    },
    handleChatChannelMessage (payload) {
      if (payload.type !== 'message_error') return
      this.sending = false
      this.$buefy.toast.open({
        message: payload.errors?.[0] || 'Не удалось отправить сообщение',
        type: 'is-danger',
      })
    },
    subscribeCable () {
      if (this.cableSubscribed) return
      this.$cable.subscribe(CHAT_CHANNEL(this.starId), CHAT_CHANNEL_NAME)
      this.cableSubscribed = true
    },
    unsubscribeCable () {
      if (!this.cableSubscribed) return
      this.$cable.unsubscribe(CHAT_CHANNEL_NAME)
      this.cableSubscribed = false
    },
    performResubscribe () {
      this.$cable.perform({
        channel: CHAT_CHANNEL_NAME,
        action: 'resubscribe',
        data: { star_id: this.starId },
      })
    },
    async handleLocationChanged ({ star_id: starId }) {
      if (starId !== this.starId) {
        this.starId = starId
        this.performResubscribe()
        await this.reloadRoomsData()
      }
      if (this.activeTab === 'global' || this.activeTab === 'system') {
        this.loadActivePlayers()
      }
    },
    async handlePrivateChatOpened (room) {
      if (!room?.id) return
      const exists = this.rooms.some(r => r.id === room.id)
      if (!exists) {
        this.rooms.push(room)
        await this.loadMessages(room.id)
      }
      this.performResubscribe()
      this.activeTab = room.id
    },
    updatePlayerHandshakeStatus (playerId, status) {
      for (const list of [this.globalPlayers, this.systemPlayers]) {
        const entry = list.find(p => p.id === playerId)
        if (entry) entry.handshake_status = status
      }
    },
    async requestPrivateChat (player) {
      const existing = this.privateRooms.find(
        r => r.user_a_id === player.id || r.user_b_id === player.id
      )
      if (existing) {
        this.activeTab = existing.id
        return
      }

      try {
        const data = await ChatService.requestPrivateChat(player.id)
        const result = data.data
        if (result.status === 'opened' && result.chat_room_id) {
          await this.reloadRoomsData()
          this.performResubscribe()
          this.activeTab = result.chat_room_id
          this.updatePlayerHandshakeStatus(player.id, 'opened')
          this.$buefy.toast.open({
            message: `Приватный чат с @${player.username} открыт`,
            type: 'is-success',
          })
        } else {
          this.updatePlayerHandshakeStatus(player.id, 'pending')
          this.$buefy.toast.open({
            message: `Ожидание ответа от @${player.username}`,
            type: 'is-info',
          })
        }
      } catch (_error) {
        this.$buefy.toast.open({
          message: 'Не удалось начать приватный чат',
          type: 'is-danger',
        })
      }
    },
  },
}
</script>

<style scoped>
.chat-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
  border-top: 2px solid #dbdbdb;
  padding: 0.5rem 1rem;
}

.chat-tabs {
  margin-bottom: 0.5rem;
}

.chat-body {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 0.75rem;
}

.chat-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.no-room {
  color: #999;
  font-size: 0.9rem;
  padding: 1rem;
}
</style>
